export interface PreDiscoveryQuestion {
  id?: string;
  theme: string;
  question: string;
  why_it_matters: string;
  what_good_answer_looks_like: string;
  priority: "H" | "M" | "L";
  owner: "client" | "pericles";
  followups?: string[];
  answer?: string;
  answerUpdatedAt?: string;
  answerUpdatedBy?: "client" | "pericles" | "unknown";
  answerSource?: "pre_discovery" | "meeting";
}

export interface PreDiscoveryGenerationResponse {
  questions: PreDiscoveryQuestion[];
  model: string;
  generatedAt: string;
}

export interface PreDiscoveryAnswerUpdate {
  id?: string;
  question?: string;
  answer: string;
  answeredBy?: "client" | "pericles" | "unknown";
}

export interface PreDiscoveryAnswerUpdateResponse {
  questions: PreDiscoveryQuestion[];
  updatedAt: string;
}

export async function generatePreDiscoveryQuestions(projectId: string): Promise<PreDiscoveryGenerationResponse> {
  if (!projectId) {
    throw new Error("projectId is required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/pre-discovery/questions/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  const payload = (await response.json()) as PreDiscoveryGenerationResponse & { error?: string };

  if (!response.ok) {
    throw new Error(payload?.error ?? `Failed to generate discovery questions (status ${response.status})`);
  }

  return payload;
}

export async function updatePreDiscoveryQuestionAnswers(
  projectId: string,
  updates: PreDiscoveryAnswerUpdate[],
): Promise<PreDiscoveryAnswerUpdateResponse> {
  if (!projectId) {
    throw new Error("projectId is required");
  }

  if (!Array.isArray(updates) || updates.length === 0) {
    throw new Error("updates are required");
  }

  const response = await fetch(`/api/projects/${encodeURIComponent(projectId)}/pre-discovery/questions/answers`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ updates }),
  });

  const payload = (await response.json()) as PreDiscoveryAnswerUpdateResponse & { error?: string };

  if (!response.ok) {
    throw new Error(payload?.error ?? `Failed to update pre-discovery answers (status ${response.status})`);
  }

  return payload;
}
